import { useCallback, useRef, useState } from "react"
import { useLazyLoadQuery, useRelayEnvironment } from "react-relay"
import type {
  FetchPolicy,
  GraphQLTaggedNode,
  OperationType,
  VariablesOf,
} from "relay-runtime"
import { fetchQuery } from "relay-runtime"

type QueryOptions = {
  fetchKey: number
  fetchPolicy: FetchPolicy
}

/**
 * Allows refetching a relay lazy load query without suspense
 * @param query - Query to be loaded lazily
 * @param variables - Variables to be used for query (and subsequent refetches)
 * @returns Data and refetch callback
 */
export const useRefetchableLazyLoadQuery = <TOperation extends OperationType>(
  query: GraphQLTaggedNode,
  variables: VariablesOf<TOperation>,
  initialOptions?: Partial<QueryOptions>,
) => {
  const [queryOptions, setQueryOptions] = useState<QueryOptions>({
    fetchKey: 0,
    fetchPolicy: "store-or-network",
    ...initialOptions,
  })

  const data = useLazyLoadQuery<TOperation>(query, variables, queryOptions)

  const environment = useRelayEnvironment()
  const isRefreshingRef = useRef(false)
  const refetch = useCallback(() => {
    if (isRefreshingRef.current) {
      return
    }

    isRefreshingRef.current = true

    fetchQuery(environment, query, variables).subscribe({
      complete: () => {
        isRefreshingRef.current = false
        setQueryOptions(prev => ({
          ...prev,
          fetchPolicy: "store-or-network",
          fetchKey: prev.fetchKey + 1,
        }))
      },
      error: () => {
        isRefreshingRef.current = false
      },
    })
  }, [environment, query, variables])

  return [data, refetch] as const
}
